import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Form, InputNumber, message, Space,
  Divider, Modal, Typography, Select, Tabs, Alert, Tag,
  Row, Col, Statistic
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  ReloadOutlined,
  PercentageOutlined,
  TrophyOutlined,
  DollarOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminOdds = () => {
  const { canWrite, isObserver } = usePermissions();

  // 觀察者樣式
  const observerStyles = `
    .observer-readonly {
      background-color: #fafafa !important;
      cursor: not-allowed;
    }
    .observer-readonly:hover {
      background-color: #f0f0f0 !important;
    }
    .observer-readonly .ant-btn {
      pointer-events: none;
      opacity: 0.6;
    }
  `;

  React.useEffect(() => {
    if (isObserver()) {
      const style = document.createElement('style');
      style.textContent = observerStyles;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, [isObserver]);
  const [odds, setOdds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [form] = Form.useForm();
  const [oddsStats, setOddsStats] = useState(null);
  const [batchUpdateModal, setBatchUpdateModal] = useState(false);
  const [batchForm] = Form.useForm();

  useEffect(() => {
    fetchOdds();
    fetchOddsStats();
  }, []);

  const fetchOdds = async () => {
    setLoading(true);
    try {
      const response = await api.get('/odds');
      setOdds(response.odds || []);
    } catch (error) {
      console.error('取得賠率資料失敗:', error);
      message.error('取得賠率資料失敗');
    }
    setLoading(false);
  };

  const fetchOddsStats = async () => {
    try {
      const response = await api.get('/odds/stats');
      setOddsStats(response);
    } catch (error) {
      console.error('取得賠率統計失敗:', error);
    }
  };

  const updateOdds = async (record, values) => {
    try {
      await api.put(`/odds/${record.id}`, values);
      message.success('賠率更新成功');
      fetchOdds();
      setEditingKey('');
    } catch (error) {
      console.error('賠率更新失敗:', error);
      message.error('賠率更新失敗');
    }
  };

  const batchUpdateOdds = async (values) => {
    try {
      await api.post('/odds/batch-update', values);
      message.success('批量更新成功');
      fetchOdds();
      setBatchUpdateModal(false);
      batchForm.resetFields();
    } catch (error) {
      console.error('批量更新失敗:', error);
      message.error('批量更新失敗');
    }
  };

  const resetOdds = async () => {
    Modal.confirm({
      title: '確認重置',
      content: '確定要將所有賠率重置為預設值嗎？此操作不可逆！',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.post('/odds/reset');
          message.success('賠率重置成功');
          fetchOdds();
        } catch (error) {
          console.error('賠率重置失敗:', error);
          message.error('賠率重置失敗');
        }
      }
    });
  };

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      odds: record.odds,
      ...record,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (record) => {
    try {
      const values = await form.validateFields();
      await updateOdds(record, values);
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const getBetTypeName = (betType, betValue) => {
    const typeNames = {
      'position': `名次`,
      'sum_value': `冠亞和值${betValue}`,
      'sum_big_small': `冠亞和值${betValue === 'big' ? '大' : '小'}`,
      'sum_odd_even': `冠亞和值${betValue === 'odd' ? '單' : '雙'}`,
      'big_small': `大、小`,
      'odd_even': `單、雙`,
      'dragon_tiger': `龍、虎`
    };
    return typeNames[betType] || `${betType}-${betValue}`;
  };

  const getOddsStatus = (odds) => {
    if (odds >= 2.0) return { color: 'green', text: '高賠率' };
    if (odds >= 1.5) return { color: 'blue', text: '中賠率' };
    return { color: 'orange', text: '低賠率' };
  };

  const columns = [
    {
      title: '投注類型',
      dataIndex: 'betType',
      key: 'betType',
      render: (betType, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{getBetTypeName(betType, record.betValue)}</Text>
          <Tag color="blue" size="small">{betType}</Tag>
        </Space>
      ),
      width: window.innerWidth <= 768 ? 120 : 150,
    },
    {
      title: '投注項目',
      dataIndex: 'betValue',
      key: 'betValue',
      render: (value) => <Text code>{value}</Text>,
      width: window.innerWidth <= 768 ? 80 : 100,
    },
    {
      title: '當前賠率',
      dataIndex: 'odds',
      key: 'odds',
      render: (odds, record) => {
        const editable = isEditing(record);
        const status = getOddsStatus(odds);
        
        return editable ? (
          <Form.Item
            name="odds"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: '請輸入賠率' },
              { type: 'number', min: 0.01, max: 100, message: '賠率必須在 0.01-100 之間' }
            ]}
          >
            <InputNumber
              step={0.01}
              precision={2}
              style={{ width: window.innerWidth <= 768 ? 80 : 100 }}
            />
          </Form.Item>
        ) : (
          <Space>
            <Text strong style={{ color: status.color }}>{odds}x</Text>
            <Tag color={status.color} size="small">{status.text}</Tag>
          </Space>
        );
      },
      width: window.innerWidth <= 768 ? 120 : 150,
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '啟用' : '停用'}
        </Tag>
      ),
      width: window.innerWidth <= 768 ? 60 : 80,
    },
    {
      title: '最後更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
      width: window.innerWidth <= 768 ? 120 : 150,
    },
    {
      title: '更新者',
      dataIndex: 'updatedByAgent',
      key: 'updatedByAgent',
      render: (agent) => agent?.displayName || '-',
      width: window.innerWidth <= 768 ? 80 : 100,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => save(record)}
            >
              保存
            </Button>
            <Button size="small" onClick={cancel}>
              取消
            </Button>
          </Space>
        ) : (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            disabled={editingKey !== '' || !canWrite()}
            onClick={() => edit(record)}
          >
            {canWrite() ? '編輯' : '查看'}
          </Button>
        );
      },
      width: window.innerWidth <= 768 ? 100 : 120,
    },
  ];

  const groupedOdds = odds.reduce((acc, odd) => {
    if (!acc[odd.betType]) {
      acc[odd.betType] = [];
    }
    acc[odd.betType].push(odd);
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center', 
        marginBottom: 24,
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? 12 : 0
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16,
          flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
        }}>
          <Title level={2} style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>賠率管理</Title>
          {isObserver() && (
            <Tag icon={<EyeOutlined />} color="orange" style={{ fontSize: window.innerWidth <= 768 ? '11px' : '12px' }}>
              {window.innerWidth <= 768 ? '觀察者' : '觀察者模式 - 僅供查看'}
            </Tag>
          )}
        </div>
        <Space wrap={window.innerWidth <= 768}>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchOdds}
            loading={loading}
            size={window.innerWidth <= 768 ? "small" : "middle"}
          >
            {window.innerWidth <= 768 ? "重新載入" : "重新載入"}
          </Button>
          {canWrite() && (
            <>
              <Button
                type="primary"
                onClick={() => setBatchUpdateModal(true)}
                size={window.innerWidth <= 768 ? "small" : "middle"}
              >
                {window.innerWidth <= 768 ? "批量調整" : "批量調整"}
              </Button>
              <Button
                danger
                onClick={resetOdds}
                size={window.innerWidth <= 768 ? "small" : "middle"}
              >
                {window.innerWidth <= 768 ? "重置預設" : "重置預設"}
              </Button>
            </>
          )}
        </Space>
      </div>

      {oddsStats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="總賠率項目"
                value={oddsStats.totalOdds}
                prefix={<PercentageOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="啟用項目"
                value={oddsStats.activeOdds}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="平均賠率"
                value={oddsStats.averageOdds}
                precision={2}
                suffix="x"
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Alert
        message={isObserver() ? "觀察者模式" : "賠率調整提醒"}
        description={isObserver() ? "您目前處於觀察者模式，只能查看賠率資料，無法進行任何修改操作。" : "賠率調整會立即生效，請謹慎操作。建議在非繁忙時段進行批量調整。"}
        type={isObserver() ? "info" : "warning"}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card style={isObserver() ? {
        backgroundColor: '#f5f5f5',
        border: '1px solid #d9d9d9',
        opacity: 0.8
      } : {}}>
        <Form form={form} component={false}>
          <Tabs
            defaultActiveKey="all"
            type="card"
            items={[
              {
                key: 'all',
                label: '全部賠率',
                children: (
                  <Table
                    bordered
                    dataSource={odds}
                    columns={columns}
                    rowClassName={(record) => isObserver() ? "editable-row observer-readonly" : "editable-row"}
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                    }}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: window.innerWidth <= 768 ? 600 : 800 }}
                    size={window.innerWidth <= 768 ? "small" : "middle"}
                  />
                )
              },
              ...Object.entries(groupedOdds).map(([betType, typeOdds]) => ({
                key: betType,
                label: getBetTypeName(betType, ''),
                children: (
                  <Table
                    bordered
                    dataSource={typeOdds}
                    columns={columns}
                    rowClassName="editable-row"
                    pagination={false}
                    loading={loading}
                    rowKey="id"
                    size={window.innerWidth <= 768 ? "small" : "middle"}
                    scroll={{ x: window.innerWidth <= 768 ? 600 : 800 }}
                  />
                )
              }))
            ]}
          />
        </Form>
      </Card>

      {/* 批量調整模態框 */}
      <Modal
        title="批量調整賠率"
        open={batchUpdateModal}
        onOk={() => batchForm.submit()}
        onCancel={() => setBatchUpdateModal(false)}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={batchUpdateOdds}
        >
          <Form.Item
            name="betType"
            label="投注類型"
            rules={[{ required: true, message: '請選擇投注類型' }]}
          >
            <Select placeholder="選擇投注類型">
              <Option value="all">全部類型</Option>
              <Option value="position">名次投注</Option>
              <Option value="sum_value">冠亞和值</Option>
              <Option value="sum_big_small">冠亞和大小</Option>
              <Option value="sum_odd_even">冠亞和單雙</Option>
              <Option value="big_small">大小</Option>
              <Option value="odd_even">單雙</Option>
              <Option value="dragon_tiger">龍虎</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="adjustmentType"
            label="調整方式"
            rules={[{ required: true, message: '請選擇調整方式' }]}
          >
            <Select placeholder="選擇調整方式">
              <Option value="multiply">乘以倍數</Option>
              <Option value="add">加上數值</Option>
              <Option value="set">設定為固定值</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="adjustmentValue"
            label="調整數值"
            rules={[
              { required: true, message: '請輸入調整數值' },
              { type: 'number', min: 0.01, message: '數值必須大於 0.01' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              step={0.01}
              precision={2}
              placeholder="請輸入調整數值"
            />
          </Form.Item>

          <Alert
            message="批量調整說明"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>乘以倍數: 將現有賠率乘以指定倍數</li>
                <li>加上數值: 將現有賠率加上指定數值</li>
                <li>設定為固定值: 將所有選中項目設為相同賠率</li>
              </ul>
            }
            type="info"
          />
        </Form>
      </Modal>
    </div>
  );
};

export default AdminOdds;
